import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePurchaseOrders } from '../../hooks/usePurchaseOrders';
import { purchaseOrdersService } from '../../services/purchaseOrdersService';
import { useAuth } from '../../context/AuthContext';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import SearchInput from '../../components/ui/SearchInput';
import ResponsiveTable from '../../components/ui/ResponsiveTable';
import Card from '../../components/ui/Card';

const PurchaseOrdersList: React.FC = () => {
  const navigate = useNavigate();
  const { orders, loading, error, refreshPurchaseOrders } = usePurchaseOrders();
  const { businessId } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.vendor && order.vendor.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = filterStatus === 'all' || order.status === filterStatus;

    return matchesSearch && matchesFilter;
  });

  const getFormattedAmount = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
        {error}
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Purchase Orders', current: true }
  ];

  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Purchase Orders</h1>
        <Button variant="primary" onClick={() => navigate('/purchase-orders/new')}>
          Create Purchase
        </Button>
      </div>

      <div className="mb-6 space-y-4">
        <div className="max-w-md">
          <SearchInput
            placeholder="Search purchase orders by PO number or vendor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'All' },
            { value: 'unpaid', label: 'Unpaid' },
            { value: 'paid', label: 'Paid' },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilterStatus(tab.value)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                filterStatus === tab.value
                  ? 'bg-primary-100 text-primary-700 border border-primary-300'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <ResponsiveTable
          headers={['PO Number', 'Vendor', 'Date', 'Status', 'Total Amount']}
          rows={filteredOrders.map(order => [
            order.po_number,
            order.vendor?.name || 'Unknown Vendor',
            new Date(order.purchase_date).toLocaleDateString(),
            <span className={`badge ${purchaseOrdersService.getStatusBadgeClass(order.status)}`}>
              {purchaseOrdersService.getStatusLabel(order.status)}
            </span>,
            getFormattedAmount(order.total_amount),
          ])}
          renderRow={(row, index) => {
            const order = filteredOrders[index];
            return (
              <tr
                key={order.id}
                onClick={() => navigate(`/purchase-orders/${order.id}`)}
                className="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row[0]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[1]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[2]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[3]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[4]}
                </td>
              </tr>
            );
          }}
          renderMobileCard={(row, index) => {
            const order = filteredOrders[index];
            return (
              <Card
                key={order.id}
                onClick={() => navigate(`/purchase-orders/${order.id}`)}
                className="mb-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        {row[0]}
                      </h3>
                      <div className="mt-2 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Vendor</p>
                          <p className="text-sm font-medium text-gray-900">{row[1]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Date</p>
                          <p className="text-sm font-medium text-gray-900">{row[2]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Status</p>
                          <div className="text-sm font-medium text-gray-900">{row[3]}</div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Amount</p>
                          <p className="text-sm font-medium text-gray-900">{row[4]}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          }}
          emptyMessage="No purchase orders found"
        />
      </div>

      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Showing {filteredOrders.length} of {orders.length} purchase orders
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" disabled>
            Previous
          </Button>
          <Button variant="outline" disabled>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrdersList;